import type {
  ProjectFileKindTag,
  ProjectQuoteType,
  ProjectFileStream,
  ProjectFileStreamKind,
} from '../../types';
import {
  normalizeKindTagForFile,
  suggestedKindTagForUpload,
  suggestedStreamKindForFile,
  isVersionTrackCandidateFile,
} from '../../utils/projectFileService';
import type { ProjectFileDetailStage } from '../../utils/projectFileVersionGroups';

export const trackKindShort = (k: ProjectFileStreamKind) => {
  if (k === 'three_d_html') return 'HTML';
  if (k === 'three_d_raw') return '3D';
  if (k === 'two_d_pdf') return '2D';
  if (k === 'timber_list_csv') return 'Timber';
  if (k === 'hardware_list_csv') return 'Hardware';
  return 'Other';
};

export type PendingVersionUploadRow = {
  clientId: string;
  file: File | null;
  /** When multiple Firestore streams match the file type, which history to attach to. */
  pickStreamId: string;
  newTitle: string;
  newKind: ProjectFileStreamKind;
  versionLabel: string;
  versionNote: string;
  /** Required before a new version can be saved. */
  detailStage: ProjectFileDetailStage | '';
  /** Timber CSV only. Keep cross-section inches as written (7.5 stays 7.5). */
  trueDimensions: boolean;
  /** 3D models only. When set, {@link cncStructureIds} is required. */
  forCnc: boolean;
  cncStructureIds: string[];
  /** Structures a NEW track covers (ignored when adding to an existing history). Empty = whole project. */
  newStructureIds: string[];
};

export function newVersionRowId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function emptyVersionUploadRow(
  streams: ProjectFileStream[],
  selectedStreamId: string | null,
): PendingVersionUploadRow {
  return {
    clientId: newVersionRowId(),
    file: null,
    pickStreamId:
      selectedStreamId && streams.some(s => s.id === selectedStreamId)
        ? selectedStreamId
        : streams[0]?.id || '',
    newTitle: '',
    newKind: 'two_d_pdf',
    versionLabel: '',
    versionNote: '',
    detailStage: '',
    trueDimensions: false,
    forCnc: false,
    cncStructureIds: [],
    newStructureIds: [],
  };
}

export function versionRowFromFile(
  file: File,
  streams: ProjectFileStream[],
  selectedStreamId: string | null,
  csvText?: string,
): PendingVersionUploadRow {
  const suggestedKind = suggestedStreamKindForFile(file, csvText);
  const matching = streams.filter(s => s.streamKind === suggestedKind);
  const baseName = file.name.replace(/\.[^/.]+$/, '') || file.name;

  if (matching.length > 0) {
    const prefer =
      selectedStreamId && matching.some(s => s.id === selectedStreamId)
        ? selectedStreamId
        : matching[0].id || '';
    return {
      clientId: newVersionRowId(),
      file,
      pickStreamId: prefer,
      newTitle: baseName,
      newKind: suggestedKind,
      versionLabel: '',
      versionNote: '',
      detailStage: '',
      trueDimensions: false,
      forCnc: false,
      cncStructureIds: [],
      newStructureIds: [],
    };
  }

  return {
    clientId: newVersionRowId(),
    file,
    pickStreamId: '',
    newTitle: baseName,
    newKind: suggestedKind,
    versionLabel: '',
    versionNote: '',
    detailStage: '',
    trueDimensions: false,
    forCnc: false,
    cncStructureIds: [],
    newStructureIds: [],
  };
}

/** UI + submit: how each version row resolves when Firestore streams exist vs batch chaining. */
export type VersionRowDisposition =
  | { kind: 'empty' }
  | { kind: 'matchedExisting'; streams: ProjectFileStream[] }
  | { kind: 'newTrackLeader'; streamKind: ProjectFileStreamKind }
  | { kind: 'newTrackFollow'; streamKind: ProjectFileStreamKind };

export function versionRowStreamKind(
  row: PendingVersionUploadRow,
  disposition: VersionRowDisposition,
): ProjectFileStreamKind | null {
  if (disposition.kind === 'newTrackLeader' || disposition.kind === 'empty') return row.newKind;
  if (disposition.kind === 'newTrackFollow') return disposition.streamKind;
  if (disposition.kind === 'matchedExisting') {
    const picked = disposition.streams.find(s => s.id === row.pickStreamId) ?? disposition.streams[0];
    return picked?.streamKind ?? row.newKind;
  }
  return null;
}

/** Pass version rows in tab/submit order (same array order as upload rows). */
export function dispositionsForVersionUploadRows(
  orderedRows: ProjectUploadRow[],
  streams: ProjectFileStream[],
): Map<string, VersionRowDisposition> {
  const map = new Map<string, VersionRowDisposition>();
  /** First PDF vs second PDF in batch — keyed by inferred stream kind from file, not the TYPE dropdown. */
  const seenRoutingKindWithoutFirestore = new Set<ProjectFileStreamKind>();

  const versionRows = orderedRows.filter(
    (r): r is ProjectUploadRow & { rowKind: 'version' } => r.rowKind === 'version',
  );

  for (const row of versionRows) {
    if (!row.file) {
      map.set(row.clientId, { kind: 'empty' });
      continue;
    }
    const matching = streams.filter(s => s.streamKind === row.newKind);
    if (matching.length > 0) {
      map.set(row.clientId, { kind: 'matchedExisting', streams: matching });
      continue;
    }

    const routingKind = row.newKind;
    if (seenRoutingKindWithoutFirestore.has(routingKind)) {
      map.set(row.clientId, { kind: 'newTrackFollow', streamKind: routingKind });
    } else {
      seenRoutingKindWithoutFirestore.add(routingKind);
      map.set(row.clientId, { kind: 'newTrackLeader', streamKind: routingKind });
    }
  }
  return map;
}

/** Resolve Firestore stream id when one or more streams match the file. */
export function pickMatchingStreamIdForUpload(
  matching: ProjectFileStream[],
  hintStreamId: string | null,
  rowPickStreamId: string,
): string {
  if (matching.length === 0) return '';
  if (matching.length === 1) return matching[0].id || '';
  const fromRow = rowPickStreamId && matching.some(s => s.id === rowPickStreamId) ? rowPickStreamId : '';
  const fromHint = hintStreamId && matching.some(s => s.id === hintStreamId) ? hintStreamId : '';
  return fromRow || fromHint || matching[0].id || '';
}

/** Upload modal scope: formal version history vs everyday project documents vs quotes. */
export type ProjectUploadModalKind = 'version' | 'general' | 'quote';

/** Single upload dialog: version track vs general project file per row */
export type ProjectUploadRow =
  | ({ rowKind: 'version' } & PendingVersionUploadRow)
  | {
      rowKind: 'general';
      clientId: string;
      file: File | null;
      kindTag: ProjectFileKindTag;
      /** Virtual project folder (`projectFileFolders.ts`); '' = Unsorted. */
      folderPath: string;
    }
  | {
      rowKind: 'quote';
      clientId: string;
      file: File | null;
      quoteType: ProjectQuoteType;
    };

export const QUOTE_TYPE_OPTIONS: { value: ProjectQuoteType; label: string }[] = [
  { value: 'timber', label: 'Timber' },
  { value: 'cnc', label: 'CNC' },
  { value: 'timber_and_cnc', label: 'Timber & CNC' },
  { value: 'hardware_and_fasteners', label: 'Hardware & Fasteners' },
];

export function emptyGeneralUploadRow(folderPath = ''): ProjectUploadRow {
  return {
    rowKind: 'general',
    clientId: newVersionRowId(),
    file: null,
    kindTag: 'none',
    folderPath,
  };
}

export function emptyQuoteUploadRow(): ProjectUploadRow {
  return {
    rowKind: 'quote',
    clientId: newVersionRowId(),
    file: null,
    quoteType: 'timber',
  };
}

export function quoteRowFromFile(file: File): ProjectUploadRow {
  return {
    rowKind: 'quote',
    clientId: newVersionRowId(),
    file,
    quoteType: 'timber',
  };
}

export function emptyVersionUploadRowUnified(
  streams: ProjectFileStream[],
  selectedStreamId: string | null,
): ProjectUploadRow {
  return { rowKind: 'version', ...emptyVersionUploadRow(streams, selectedStreamId) };
}

export function unifiedRowFromFile(
  file: File,
  streams: ProjectFileStream[],
  selectedStreamId: string | null,
  csvText?: string,
  folderPath = '',
): ProjectUploadRow {
  if (isVersionTrackCandidateFile(file)) {
    return { rowKind: 'version', ...versionRowFromFile(file, streams, selectedStreamId, csvText) };
  }
  const suggested = suggestedKindTagForUpload(file, csvText);
  return {
    rowKind: 'general',
    clientId: newVersionRowId(),
    file,
    kindTag: normalizeKindTagForFile(file, suggested),
    folderPath,
  };
}

/** General file row when the modal is in “General document” mode (any file type). */
export function generalRowFromFile(file: File, csvText?: string, folderPath = ''): ProjectUploadRow {
  const suggested = suggestedKindTagForUpload(file, csvText);
  return {
    rowKind: 'general',
    clientId: newVersionRowId(),
    file,
    kindTag: normalizeKindTagForFile(file, suggested),
    folderPath,
  };
}

export function applyUploadRowKind(
  row: ProjectUploadRow,
  nextKind: ProjectUploadModalKind,
  streams: ProjectFileStream[],
  selectedStreamId: string | null,
  folderPath = '',
): ProjectUploadRow {
  if (nextKind === 'quote') {
    if (row.rowKind === 'quote') return row;
    const file = row.rowKind === 'version' || row.rowKind === 'general' ? row.file : null;
    if (!file) return { ...emptyQuoteUploadRow(), clientId: row.clientId };
    return { rowKind: 'quote', clientId: row.clientId, file, quoteType: 'timber' };
  }
  if (nextKind === 'general') {
    if (row.rowKind === 'general') return row;
    const file = row.file;
    if (!file) return { ...emptyGeneralUploadRow(folderPath), clientId: row.clientId };
    const suggested = suggestedKindTagForUpload(file);
    return {
      rowKind: 'general',
      clientId: row.clientId,
      file,
      kindTag: normalizeKindTagForFile(file, suggested),
      folderPath,
    };
  }
  if (row.rowKind === 'version') return row;
  if (!row.file)
    return {
      rowKind: 'version',
      ...emptyVersionUploadRow(streams, selectedStreamId),
      clientId: row.clientId,
    };
  if (!isVersionTrackCandidateFile(row.file)) {
    window.alert(
      'Version history only accepts PDF, HTML, 3D models (Cadwork, SketchUp, IFC, STEP, DXF, SAT), or CSV. Keep this row as a general file or attach a matching file.',
    );
    return row;
  }
  const v = versionRowFromFile(row.file, streams, selectedStreamId);
  return { rowKind: 'version', ...v, clientId: row.clientId };
}
